import { Link } from "@tanstack/react-router";
import { Globe } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { LANGS, LANG_LABEL, pathForLang, type Lang } from "@/i18n/translations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function LanguageSwitcher({ variant = "desktop" }: { variant?: "desktop" | "mobile" }) {
  const { lang, t } = useI18n();

  if (variant === "mobile") {
    return (
      <div className="flex items-center gap-1.5 rounded-full border border-border/70 bg-night-deep/60 p-1">
        {LANGS.map((l) => (
          <Link
            key={l}
            to={pathForLang(l)}
            aria-label={LANG_LABEL[l]}
            aria-current={l === lang ? "true" : undefined}
            className={`rounded-full px-2.5 py-1 font-mono-cinema text-[10px] uppercase transition-colors ${
              l === lang
                ? "bg-gold text-gold-foreground"
                : "text-foreground/70 hover:text-gold"
            }`}
          >
            {l}
          </Link>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t.common.language}
        className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border/70 bg-night-deep/40 px-3 font-mono-cinema text-[10px] uppercase text-foreground/85 transition-colors hover:border-gold/50 hover:text-gold"
      >
        <Globe className="h-3.5 w-3.5" />
        {lang}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {LANGS.map((l) => (
          <DropdownMenuItem key={l} asChild>
            <Link
              to={pathForLang(l)}
              aria-current={l === lang ? "true" : undefined}
              className={`flex w-full items-center justify-between ${
                l === lang ? "text-gold" : ""
              }`}
            >
              <span>{LANG_LABEL[l]}</span>
              <span className="font-mono-cinema text-[10px] uppercase opacity-70">{l}</span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function languageHreflangLinks(): Array<{ rel: string; hrefLang: string; href: string }> {
  const items = LANGS.map((l) => ({
    rel: "alternate",
    hrefLang: l as string,
    href: `https://openluchtfilmfestival.nl${pathForLang(l)}`,
  }));
  items.push({ rel: "alternate", hrefLang: "x-default", href: `https://openluchtfilmfestival.nl/` });
  return items;
}

export const allLangs: Lang[] = [...LANGS];